import { useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';

import styled from 'styled-components';

import { createForm, useCreateFormStore, useFormContext } from '@components/form';
import { IconDocument } from '@components/icons/document';
import { IconOpen } from '@components/icons/open';
import { IconSupport } from '@components/icons/support';
import {
  MaxLengthCounter,
  MaxLengthCounterProps,
} from '@components/max-length-counter/max-length-counter';
import { Sidebar, forceHideSidebar, useInfoButton, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useToaster } from '@components/toaster';
import { useIntercom } from '@hooks/use-intercom';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  DocumentErrorResponse,
  useCreateDocumentTemplate,
  useEditDocumentTemplate,
} from 'api/documents/hooks';
import { CreateUpdateDocumentTemplate, DocumentTemplate } from 'api/documents/requests';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';
import { FormWithUnsavedChangesPrompt } from 'ui/journey-builder/unsaved-changes-prompt';

const Form = createForm<CreateUpdateDocumentTemplate>();

export const useDocumentAddEditSidebar = () =>
  useSidebar(DocumentAddEditSidebar, { variant: 'REGULAR' });

const DocumentAddEditSidebar = ({ document }: { document?: DocumentTemplate }) => {
  const { t } = useScopedTranslate(locale);
  const { showError } = useToaster();
  const [isCharacterLimitExceeded, setIsCharacterLimitExceeded] = useState(false);
  const { workspaceId } = useCurrentWorkspace();
  const { showMessenger, isEnabled: isIntercomEnabled } = useIntercom();
  const { InfoButton, setIsEnabled } = useInfoButton({ renderComponent: <Info /> });
  const onError = (error: DocumentErrorResponse) => {
    const errorMessage = error.response?.data?.error ?? '';
    showError(errorMessage, {
      customButtons: [
        {
          text: t('documentGuidlines'),
          onClick: () => setIsEnabled(true),
          icon: <IconOpen />,
        },
        ...(isIntercomEnabled
          ? [
              {
                text: t('supportChat'),
                onClick: showMessenger,
                icon: <IconSupport />,
              },
            ]
          : []),
      ],
    });
  };
  const createMutation = useCreateDocumentTemplate({ onError });
  const editMutation = useEditDocumentTemplate({ onError });
  const navigate = useNavigate();
  const formStore = useCreateFormStore();
  const isDirty = formStore.useIsDirty();

  const isEdit = Boolean(document);

  const handleSubmit = async (data: CreateUpdateDocumentTemplate) => {
    if (isEdit) {
      await editMutation.mutateAsync({
        id: document!.shortId,
        ...data,
        file: data.file ?? undefined,
      });
      forceHideSidebar();
    } else {
      const response = await createMutation.mutateAsync({
        ...data,
        workspace: workspaceId,
        status: 'active',
      });
      navigate(
        generatePath(routePaths.worksspaceDocumentDetail, {
          workspaceId,
          documentId: response.shortId,
          '*': null,
        })
      );
    }
  };

  return (
    <Sidebar>
      <Sidebar.SimpleHeader
        icon={IconDocument}
        iconColor="elementBlue100"
        title={isEdit ? t('editDocument') : t('createDocument')}
      >
        <InfoButton />
      </Sidebar.SimpleHeader>
      <Sidebar.Content>
        <FormWithUnsavedChangesPrompt onSubmit={handleSubmit} store={formStore}>
          <Form.FileDropzone
            id="file"
            label={t('file')}
            fileTypes={['docx']}
            validators={isEdit ? [] : ['required']}
            defaultPath={document?.url}
          />
          <Form.TextField
            id="name"
            label={t('name')}
            validators={['required']}
            defaultValue={document?.name}
          />
          <Description
            defaultValue={document?.description ?? ''}
            onExceedStatusChange={setIsCharacterLimitExceeded}
          />

          <Sidebar.Footer>
            <Form.Actions>
              <Form.Reset
                text={t('core.cancel')}
                variant="secondary"
                onClick={forceHideSidebar}
                isDisabled={createMutation.isLoading || editMutation.isLoading}
              />
              <Form.Submit
                text={t('core.save')}
                variant="primary-blue"
                isDisabled={
                  !isDirty ||
                  createMutation.isLoading ||
                  editMutation.isLoading ||
                  isCharacterLimitExceeded
                }
              />
            </Form.Actions>
          </Sidebar.Footer>
        </FormWithUnsavedChangesPrompt>
      </Sidebar.Content>
    </Sidebar>
  );
};

const Description = ({
  onExceedStatusChange,
  defaultValue,
}: {
  onExceedStatusChange: MaxLengthCounterProps['onExceedStatusChange'];
  defaultValue: string;
}) => {
  const { t } = useScopedTranslate(locale);
  const {
    store: { useFieldValue },
  } = useFormContext();
  const description = (useFieldValue('description') || '') as string;

  return (
    <Stack space={1}>
      <Form.Textarea id="description" label={t('description')} defaultValue={defaultValue} />
      <MaxLengthCounter
        currentLength={description.length}
        maxLength={255}
        onExceedStatusChange={onExceedStatusChange}
      />
    </Stack>
  );
};

export const Info = () => {
  const { t } = useScopedTranslate(locale);
  const guidelines = [
    { correctText: '{{name}}', incorrectText: '{name}' },
    { correctText: '{{full_name}}', incorrectText: '{{full name}}' },
    { correctText: '{{full_name}}', incorrectText: '{{Full_name}}' },
    { correctText: '{{first_day}}', incorrectText: '{{1_day}}' },
  ];

  return (
    <Stack space={4}>
      <Text font="bold16" text={t('aboutDocuments')} />
      <Text font="regular14" text={t('infoAboutOne')} />
      <Text
        font="regular14"
        text={styledTranslate({
          key: 'infoAboutTwo',
          variables: {
            variable: {
              text: t('infoVariable'),
              font: 'bold14',
            },
          },
          translations: locale,
        })}
      />
      <Text font="bold14" text={t('importantGuidlines')} />

      <Stack space={1}>
        <List>
          {guidelines.map(({ correctText, incorrectText }, index) => (
            <li>
              <Text
                font="regular14"
                text={styledTranslate({
                  key: `guideline_${index + 1}`,
                  translations: locale,
                  variables: {
                    correctVariable: {
                      text: correctText,
                      font: 'bold14',
                      color: 'green100',
                    },
                    incorrectVariable: {
                      text: incorrectText,
                      font: 'bold14',
                      color: 'red100',
                    },
                  },
                })}
              />
            </li>
          ))}
        </List>
        <Text font="regular14" text={t('infoAboutThree')} />
      </Stack>
    </Stack>
  );
};

const List = styled.ol({
  font: theme.font.regular14,
  color: theme.color.textPrimary,
  margin: 0,
  paddingLeft: pxToRem(24),
  '> li': {
    marginBottom: pxToRem(16),
  },
});

const locale = {
  en: {
    createDocument: 'Add document',
    editDocument: 'Update document',
    name: 'Name',
    description: 'Description',
    file: 'File',
    status: 'Status',
    active: 'Active',
    disabled: 'Disabled',
    aboutDocuments: 'About documents',
    infoAboutOne:
      'When adding a new document template the system will automatically retrieve variable information specified in the document.',
    infoAboutTwo:
      'This means that wherever the text {variable} is located in the .docx file the system will give you the ability to manage those fields to use in future journeys.',
    infoAboutThree: 'When using long variable names we recommend using underlines between words.',
    infoVariable: '{{something}}',
    importantGuidlines: 'Import guidelines - preparing your .docx file',
    guideline_1:
      'Make sure that you have two curly braces on each side\n{correctVariable} not {incorrectVariable}',
    guideline_2:
      'Variables should not have any empty spaces\n{correctVariable} not {incorrectVariable}',
    guideline_3: 'Use lower case for all variable texts\n{correctVariable} not {incorrectVariable}',
    guideline_4: 'Do not start with a number\n{correctVariable} not {incorrectVariable}',
    documentGuidlines: 'Document guidelines',
    supportChat: 'Support chat',
  },
};
